/**
 * Adoption claim form
 * Validates each field with its own linked message, moves focus to the first problem,
 * resizes large photos before upload, sends the claim and shows the thank-you.
 */
(function () {
  'use strict';

  var MAX_FILE_SIZE = 4 * 1024 * 1024; // Vercel rejects request bodies over 4.5MB
  var RESIZE_ABOVE = 1.5 * 1024 * 1024; // photos above this are resized before upload
  var RESIZE_EDGE = 2400; // long edge in px, still sharp enough to read a certificate
  var REQUEST_TIMEOUT = 60000;
  var CLAIM_WINDOW_DAYS = 60;
  var ALLOWED_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
  var ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf'];
  var SUPPORT_EMAIL = 'contact@sprouties.ae';

  var form = document.querySelector('.adopt-form');
  if (!form) return;

  var card = form.closest('.adopt-form-card') || document;
  var apiEndpoint = form.dataset.apiEndpoint;
  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  var fileInput = form.querySelector('[name="certificate"]');
  var upload = form.querySelector('[data-adopt-upload]');
  var drop = form.querySelector('[data-adopt-drop]');
  var fileRow = form.querySelector('[data-adopt-file]');
  var fileName = form.querySelector('[data-adopt-file-name]');
  var fileRemove = form.querySelector('[data-adopt-file-remove]');
  var progress = form.querySelector('[data-adopt-progress]');
  var progressBar = form.querySelector('[data-adopt-progress-bar]');
  var alertEl = form.querySelector('[data-adopt-alert]');
  var submitBtn = form.querySelector('[data-adopt-submit]');
  var submitText = form.querySelector('[data-adopt-submit-text]');
  var submitLoading = form.querySelector('[data-adopt-submit-loading]');
  var intro = card.querySelector('[data-adopt-intro]');
  var success = card.querySelector('[data-adopt-success]');
  var successHeading = card.querySelector('[data-adopt-success-heading]');
  var dateInput = form.querySelector('[name="adoption_date"]');

  var selectedFile = null;
  var fileError = '';
  var sending = false;
  var fileToken = 0;
  var preparing = false;

  // -----------------------------------------------------------------------
  // Adoption date: within the last 60 days, never in the future
  // -----------------------------------------------------------------------

  // Judged in Gulf time, as the server judges it, whatever the device's timezone
  function dubaiToday() {
    try {
      var parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dubai', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
      var get = function (type) {
        for (var i = 0; i < parts.length; i++) if (parts[i].type === type) return parts[i].value;
        return '';
      };
      return get('year') + '-' + get('month') + '-' + get('day');
    } catch (err) {
      // Dubai is UTC+4 all year, with no daylight saving
      return new Date(Date.now() + 4 * 3600 * 1000).toISOString().slice(0, 10);
    }
  }

  function shiftDays(iso, days) {
    var p = iso.split('-');
    return new Date(Date.UTC(+p[0], +p[1] - 1, +p[2] + days)).toISOString().slice(0, 10);
  }

  var maxDate = '';
  var minDate = '';
  // Recomputed on load, on returning to the page and on every submit, so a tab left open
  // past midnight still uses today's window
  function refreshDateWindow() {
    maxDate = dubaiToday();
    minDate = shiftDays(maxDate, -CLAIM_WINDOW_DAYS);
    if (dateInput) {
      dateInput.min = minDate;
      dateInput.max = maxDate;
    }
  }
  refreshDateWindow();

  function syncDateEmpty() {
    if (dateInput) dateInput.classList.toggle('adopt-form__input--empty', !dateInput.value);
  }
  if (dateInput) {
    syncDateEmpty();
    dateInput.addEventListener('input', syncDateEmpty);
    dateInput.addEventListener('change', syncDateEmpty);
  }
  window.addEventListener('pageshow', function () {
    refreshDateWindow();
    syncDateEmpty();
  });

  // -----------------------------------------------------------------------
  // Validation
  // -----------------------------------------------------------------------

  function value(name) {
    var el = form.querySelector('[name="' + name + '"]');
    return el ? el.value.trim() : '';
  }

  function validPhone(v) {
    var cleaned = v.replace(/[\s\-()]/g, '');
    return /^\+971\d{8,9}$/.test(cleaned) ||
      /^05\d{8}$/.test(cleaned) ||
      /^5\d{8}$/.test(cleaned) ||
      /^0[2-9]\d{7,8}$/.test(cleaned);
  }

  var rules = {
    cat_name: function () {
      return value('cat_name') ? '' : "Please add your cat's name.";
    },
    adoption_date: function () {
      var v = value('adoption_date');
      if (!v) return 'Please add the adoption date.';
      if (v > maxDate) return "The adoption date can't be in the future.";
      if (v < minDate) return 'The free month is for cats adopted in the last 60 days. <a href="/pages/start">Find the right box for your cat</a>';
      return '';
    },
    rescue_org: function () {
      return value('rescue_org') ? '' : 'Please add the shelter or rescue organization.';
    },
    certificate: function () {
      if (fileError) return fileError;
      return selectedFile ? '' : 'Please add a photo or scan of the adoption certificate.';
    },
    full_name: function () {
      return value('full_name') ? '' : 'Please add your full name.';
    },
    email: function () {
      var v = value('email');
      if (!v) return 'Please add your email.';
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? '' : 'Please check your email address, for example name@example.com.';
    },
    phone: function () {
      var v = value('phone');
      if (!v) return 'Please add your phone number.';
      return validPhone(v) ? '' : 'Please use a UAE number, for example 050 123 4567.';
    },
    consent: function () {
      var el = form.querySelector('[name="consent"]');
      return el && el.checked ? '' : 'Please tick the box to agree to the privacy policy.';
    }
  };
  var order = ['cat_name', 'adoption_date', 'rescue_org', 'certificate', 'full_name', 'email', 'phone', 'consent'];

  function control(name) {
    return form.querySelector('[name="' + name + '"]');
  }

  function setFieldError(name, message) {
    var el = control(name);
    var errorEl = form.querySelector('[data-error-for="' + name + '"]');
    if (el) {
      if (message) el.setAttribute('aria-invalid', 'true');
      else el.removeAttribute('aria-invalid');
    }
    if (name === 'certificate' && upload) upload.classList.toggle('adopt-form__upload--invalid', !!message);
    if (errorEl) {
      // Messages are our own strings; the only markup is the link in the date message
      errorEl.innerHTML = message || '';
      errorEl.hidden = !message;
    }
  }

  function check(name) {
    var message = rules[name]();
    setFieldError(name, message);
    return message;
  }

  function validateAll() {
    var first = null;
    order.forEach(function (name) {
      if (check(name) && !first) first = name;
    });
    return first;
  }

  function focusField(name) {
    var el = control(name);
    if (!el) return;
    var target = name === 'certificate' ? (selectedFile ? fileRow : drop) : el;
    el.focus({ preventScroll: true });
    if (target && target.scrollIntoView) {
      target.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  }

  // Re-check a field as it changes once it has shown an error, and check formats on leaving
  form.addEventListener('input', function (e) {
    var name = e.target.name;
    if (rules[name] && e.target.getAttribute('aria-invalid') === 'true') check(name);
    clearAlert();
  });
  form.addEventListener('change', function (e) {
    var name = e.target.name;
    if (name === 'certificate') return;
    if (rules[name] && (e.target.getAttribute('aria-invalid') === 'true' || e.target.type === 'checkbox' || e.target.type === 'date')) {
      if (e.target.type === 'checkbox' && !e.target.checked && e.target.getAttribute('aria-invalid') !== 'true') return;
      check(name);
    }
  });
  ['email', 'phone', 'adoption_date'].forEach(function (name) {
    var el = control(name);
    if (!el) return;
    el.addEventListener('blur', function () {
      if (el.value.trim()) check(name);
    });
  });

  // -----------------------------------------------------------------------
  // Certificate: pick, drop, resize, remove
  // -----------------------------------------------------------------------

  function extension(name) {
    var parts = String(name || '').toLowerCase().split('.');
    return parts.length > 1 ? parts.pop() : '';
  }

  function formatSize(bytes) {
    if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return Math.max(1, Math.round(bytes / 1024)) + ' KB';
  }

  function resizePhoto(file) {
    return new Promise(function (resolve) {
      var isPhoto = file.type === 'image/jpeg' || file.type === 'image/png' || /^(jpe?g|png)$/.test(extension(file.name));
      if (!isPhoto || file.size <= RESIZE_ABOVE || !window.URL || !document.createElement('canvas').toBlob) {
        resolve(file);
        return;
      }
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        try {
          var scale = Math.min(1, RESIZE_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
          var w = Math.max(1, Math.round(img.naturalWidth * scale));
          var h = Math.max(1, Math.round(img.naturalHeight * scale));
          var canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          var ctx = canvas.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, w, h);
          ctx.drawImage(img, 0, 0, w, h);
          canvas.toBlob(function (blob) {
            URL.revokeObjectURL(url);
            if (!blob || blob.size >= file.size) {
              resolve(file);
              return;
            }
            var name = String(file.name || 'certificate').replace(/\.[^.]+$/, '') + '.jpg';
            var out;
            try {
              out = new File([blob], name, { type: 'image/jpeg' });
            } catch (err) {
              out = blob;
              out.name = name;
            }
            resolve(out);
          }, 'image/jpeg', 0.85);
        } catch (err) {
          URL.revokeObjectURL(url);
          resolve(file);
        }
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        resolve(file);
      };
      img.src = url;
    });
  }

  function showFile(file) {
    if (fileName) fileName.textContent = (file.name || 'certificate') + ' (' + formatSize(file.size) + ')';
    if (drop) drop.hidden = true;
    if (fileRow) fileRow.hidden = false;
  }

  function clearFile() {
    fileToken++; // any resize still running belongs to a file that is gone
    preparing = false;
    selectedFile = null;
    fileError = '';
    if (fileInput) fileInput.value = '';
    if (fileRow) fileRow.hidden = true;
    if (drop) drop.hidden = false;
    if (progress) progress.hidden = true;
    setProgress(0);
  }

  function handleFile(file) {
    clearAlert();
    fileError = '';
    if (ALLOWED_TYPES.indexOf(file.type) === -1 && ALLOWED_EXTENSIONS.indexOf(extension(file.name)) === -1) {
      clearFile();
      fileError = 'Please add a JPG, PNG or PDF file.';
      check('certificate');
      return;
    }
    // Only the newest choice may land: an older resize that finishes later is ignored
    clearFile();
    var token = fileToken;
    preparing = true;
    if (fileName) fileName.textContent = 'Preparing ' + (file.name || 'your photo') + '…';
    if (drop) drop.hidden = true;
    if (fileRow) fileRow.hidden = false;
    resizePhoto(file).then(function (ready) {
      if (token !== fileToken) return;
      preparing = false;
      if (ready.size > MAX_FILE_SIZE) {
        var isPdf = file.type === 'application/pdf' || extension(file.name) === 'pdf';
        clearFile();
        fileError = isPdf
          ? 'This PDF is over 4 MB. A photo of the certificate works too.'
          : "This photo is over 4 MB and we couldn't resize it. Please try a smaller photo.";
        check('certificate');
        return;
      }
      selectedFile = ready;
      showFile(ready);
      check('certificate');
    });
  }

  if (fileInput) {
    fileInput.addEventListener('change', function () {
      if (this.files && this.files[0]) handleFile(this.files[0]);
    });
  }

  if (drop) {
    drop.addEventListener('dragover', function (e) {
      e.preventDefault();
      drop.classList.add('adopt-form__drop--over');
    });
    drop.addEventListener('dragleave', function () {
      drop.classList.remove('adopt-form__drop--over');
    });
    drop.addEventListener('drop', function (e) {
      e.preventDefault();
      drop.classList.remove('adopt-form__drop--over');
      if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
    });
  }

  if (fileRemove) {
    fileRemove.addEventListener('click', function (e) {
      e.preventDefault();
      clearFile();
      setFieldError('certificate', '');
      if (fileInput) fileInput.focus();
    });
  }

  // -----------------------------------------------------------------------
  // Sending
  // -----------------------------------------------------------------------

  function setProgress(fraction) {
    if (progressBar) progressBar.style.transform = 'scaleX(' + Math.max(0, Math.min(1, fraction)) + ')';
  }

  function setLoading(loading) {
    sending = loading;
    if (submitBtn) {
      submitBtn.disabled = loading;
      submitBtn.setAttribute('aria-busy', loading ? 'true' : 'false');
    }
    if (submitText) submitText.hidden = loading;
    if (submitLoading) submitLoading.hidden = !loading;
  }

  function showAlert(message) {
    if (!alertEl) return;
    alertEl.innerHTML = message;
    if (alertEl.scrollIntoView) alertEl.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  function clearAlert() {
    if (alertEl && alertEl.innerHTML) alertEl.innerHTML = '';
  }

  var supportLine = 'Something went wrong on our side. Please try again, or email us at <a href="mailto:' + SUPPORT_EMAIL + '">' + SUPPORT_EMAIL + '</a>.';

  // Server messages are written for developers, so map the ones we know to a field
  function handleServerError(status, message) {
    var m = String(message || '').toLowerCase();
    if (status === 413) {
      clearFile();
      fileError = 'This file is too large to send. Please try a smaller photo.';
      check('certificate');
      focusField('certificate');
      return;
    }
    if (status === 429) {
      showAlert("We've already had several claims with these details in the last hour. Please try again in an hour, or email us at <a href=\"mailto:" + SUPPORT_EMAIL + '">' + SUPPORT_EMAIL + '</a>.');
      return;
    }
    if ((status === 400 || status === 422) && m.indexOf('missing') === -1) {
      var map = [['phone', 'phone', 'Please use a UAE number, for example 050 123 4567.'],
                 ['email', 'email', 'Please check your email address, for example name@example.com.'],
                 ['date', 'adoption_date', null],
                 ['consent', 'consent', 'Please tick the box to agree to the privacy policy.'],
                 ['certificate', 'certificate', null],
                 ['file', 'certificate', null]];
      for (var i = 0; i < map.length; i++) {
        if (m.indexOf(map[i][0]) !== -1) {
          var name = map[i][1];
          if (name === 'certificate') {
            clearFile();
            fileError = 'Please add the certificate again as a JPG, PNG or PDF up to 4 MB.';
          }
          var own = map[i][2] || rules[name]() || (name === 'adoption_date' ? 'Please check the adoption date.' : '');
          setFieldError(name, own);
          focusField(name);
          return;
        }
      }
    }
    showAlert(supportLine);
  }

  function submitClaim() {
    setLoading(true);
    clearAlert();

    var formData = new FormData();
    formData.append('full_name', value('full_name'));
    formData.append('email', value('email'));
    formData.append('phone', value('phone'));
    formData.append('cat_name', value('cat_name'));
    formData.append('rescue_org', value('rescue_org'));
    formData.append('adoption_date', value('adoption_date'));
    formData.append('certificate', selectedFile, selectedFile.name || 'certificate');
    var hp = form.querySelector('[name="website"]');
    if (hp) formData.append('website', hp.value);
    formData.append('consent', 'on');

    if (progress) progress.hidden = false;
    setProgress(0);

    var xhr = new XMLHttpRequest();
    xhr.timeout = REQUEST_TIMEOUT;

    xhr.upload.addEventListener('progress', function (e) {
      if (e.lengthComputable) setProgress(e.loaded / e.total);
    });

    xhr.addEventListener('load', function () {
      if (xhr.status >= 200 && xhr.status < 300) {
        showSuccess();
        return;
      }
      setLoading(false);
      if (progress) progress.hidden = true;
      var message = '';
      try {
        var res = JSON.parse(xhr.responseText);
        message = res && (res.error || res.message) || '';
      } catch (err) {}
      handleServerError(xhr.status, message);
    });

    xhr.addEventListener('error', function () {
      setLoading(false);
      if (progress) progress.hidden = true;
      showAlert("We couldn't send your claim. Please check your connection and try again.");
    });

    xhr.addEventListener('timeout', function () {
      setLoading(false);
      if (progress) progress.hidden = true;
      showAlert('Sending is taking longer than it should. Please check your connection and try again.');
    });

    xhr.open('POST', apiEndpoint);
    xhr.send(formData);
  }

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    if (sending) return;
    clearAlert();
    refreshDateWindow();
    if (preparing) {
      setFieldError('certificate', 'Your photo is still being prepared. Please try again in a moment.');
      focusField('certificate');
      return;
    }
    var first = validateAll();
    if (first) {
      focusField(first);
      return;
    }
    if (!apiEndpoint) {
      showAlert(supportLine);
      return;
    }
    submitClaim();
  });

  function showSuccess() {
    form.hidden = true;
    if (intro) intro.hidden = true;
    if (success) {
      success.hidden = false;
      if (successHeading) successHeading.focus({ preventScroll: true });
      success.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  }
})();
